import { useCallback, useEffect, useState } from 'react'
import type { SiteBlockSummary } from './types'
import { api, ApiError } from '../api/client'
import { getRuntime } from './_mode'
import { captureHookFailure, recordHookOutcome } from '../observability/sentry'

export function useSiteBlocks(pageId: string | undefined) {
  const [blocks, setBlocks] = useState<Array<SiteBlockSummary & { asset_count?: number }>>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!pageId) {
      setBlocks([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    const { live } = getRuntime()
    if (!live) {
      setBlocks([])
      setLoading(false)
      recordHookOutcome('useSiteBlocks', 'demo_fallback', {
        route: '/api/site/blocks',
        rows: 0,
      })
      return
    }

    const startedAt = Date.now()
    try {
      const result = await api.site.blocks(pageId)
      const rows = (result.blocks || []).map((block) => ({
        id: block.id,
        page_id: block.page_id,
        block_type: block.block_type,
        block_order: block.block_order,
        visibility: block.visibility,
        locked: block.locked,
        content_json: block.content_json,
        style_json: block.style_json,
        asset_count: block.assets?.length ?? 0,
      }))
      setBlocks(rows)
      recordHookOutcome('useSiteBlocks', 'live_success', {
        route: '/api/site/blocks',
        rows: rows.length,
        durationMs: Date.now() - startedAt,
      })
    } catch (err) {
      const apiErr = err as ApiError
      setBlocks([])
      setError(apiErr?.message || 'site_blocks_failed')
      captureHookFailure('useSiteBlocks', 'live_error', err, {
        route: '/api/site/blocks',
        code: apiErr?.code,
        status: apiErr?.status,
      })
    } finally {
      setLoading(false)
    }
  }, [pageId])

  useEffect(() => {
    void load()
  }, [load])

  return { blocks, loading, error, refresh: load }
}
